/**
 * Video Popup Custom Element
 *
 * Opens a native fullscreen modal overlay when a user clicks [data-video-play].
 * Supports inline HTML (iframe/video) passed via the data attribute.
 * Zero external dependencies.
 *
 * @example
 * <video-popup>
 *   <a href="..." data-video-play="<html>">WATCH</a>
 * </video-popup>
 */

if (!customElements.get('video-popup')) {
  customElements.define(
    'video-popup',
    class VideoPopup extends HTMLElement {
      constructor() {
        super();
        this.onButtonClick = this.onButtonClick.bind(this);
        this.onCloseClick = this.onCloseClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onBackdropClick = this.onBackdropClick.bind(this);
      }

      connectedCallback() {
        this.buttons = this.querySelectorAll('[data-video-play]');

        for (const button of this.buttons) {
          button.addEventListener('click', this.onButtonClick);
        }
      }

      disconnectedCallback() {
        for (const button of this.buttons) {
          button.removeEventListener('click', this.onButtonClick);
        }
        this.destroyModal();
      }

      onButtonClick(e) {
        const button = e.currentTarget;
        const html = button.getAttribute('data-video-play');
        if (!html || !html.trim()) return;

        e.preventDefault();
        this.lastFocusedElement = button;
        this.openModal(html);
      }

      openModal(html) {
        this.modal = document.createElement('div');
        this.modal.className = 'video-modal';
        this.modal.setAttribute('role', 'dialog');
        this.modal.setAttribute('aria-modal', 'true');

        // Close sits outside the content box so it anchors to the viewport corner.
        this.modal.innerHTML = `
          <div class="video-modal__backdrop"></div>
          <div class="video-modal__content">
            <div class="video-modal__media">${html}</div>
          </div>
          <button class="video-modal__close" type="button" aria-label="Close" title="Close (Esc)">
            <svg class="video-modal__close-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24">
              <path
                d="M6.758 17.243 12.001 12m5.243-5.243L12 12m0 0L6.758 6.757M12.001 12l5.243 5.243"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        `;

        document.body.appendChild(this.modal);
        document.body.style.overflow = 'hidden';

        const closeBtn = this.modal.querySelector('.video-modal__close');
        const backdrop = this.modal.querySelector('.video-modal__backdrop');

        closeBtn.addEventListener('click', this.onCloseClick);
        backdrop.addEventListener('click', this.onBackdropClick);
        document.addEventListener('keydown', this.onKeyDown);

        requestAnimationFrame(() => {
          this.modal.classList.add('video-modal--open');
          closeBtn.focus();
        });
      }

      onCloseClick() {
        this.destroyModal();
      }

      onBackdropClick() {
        this.destroyModal();
      }

      onKeyDown(e) {
        if (e.key === 'Escape') {
          this.destroyModal();
        }
      }

      destroyModal() {
        if (!this.modal) return;

        document.removeEventListener('keydown', this.onKeyDown);

        const media = this.modal.querySelector('iframe, video');
        if (media) {
          if (media.tagName === 'VIDEO') {
            media.pause();
            media.src = '';
          }
          media.remove();
        }

        this.modal.remove();
        this.modal = null;
        document.body.style.overflow = '';

        this.lastFocusedElement?.focus();
      }
    }
  );
}
